import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import DailyTracker from './components/DailyTracker';
import ProjectTracker from './components/ProjectTracker';
import AccessGate from './components/AccessGate';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [hasAccess, setHasAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  useEffect(() => {
    // Check if user has valid access
    const checkAccess = () => {
      const accessGranted = localStorage.getItem('gospelwise-access');
      const accessTimestamp = localStorage.getItem('gospelwise-access-timestamp');
      
      if (accessGranted === 'granted' && accessTimestamp) {
        // Optional: Check if access is still valid (e.g., within 30 days)
        const accessDate = new Date(accessTimestamp);
        const now = new Date();
        const daysDiff = (now - accessDate) / (1000 * 60 * 60 * 24);
        
        // Access is valid for 365 days (1 year)
        if (daysDiff <= 365) {
          setHasAccess(true);
        } else {
          // Access expired, clear localStorage
          localStorage.removeItem('gospelwise-access');
          localStorage.removeItem('gospelwise-access-code');
          localStorage.removeItem('gospelwise-access-timestamp');
          setHasAccess(false);
        }
      } else {
        setHasAccess(false);
      }
      
      setIsCheckingAccess(false);
    };

    checkAccess();
  }, []);

  const handleAccessGranted = () => {
    setHasAccess(true);
  };

  // Show loading state while checking access
  if (isCheckingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#ff8057]/30 border-t-[#ff8057] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading GospelWise...</p>
        </div>
      </div>
    );
  }

  // Show access gate if user doesn't have access
  if (!hasAccess) {
    return <AccessGate onAccessGranted={handleAccessGranted} />;
  }

  // Main application
  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50">
        <Header currentView={currentView} setCurrentView={setCurrentView} />
        
        <main className="container mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {currentView === 'dashboard' && <Dashboard setCurrentView={setCurrentView} />}
              {currentView === 'daily' && <DailyTracker />}
              {currentView === 'project' && <ProjectTracker />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </Router>
  );
}

export default App;